import { create } from "zustand"

/** One Seam API call the console made, from request to response. */
export interface ApiCall {
  id: string
  method: string
  /** The endpoint path, such as `/devices/list`. */
  path: string
  /** The JSON body sent, or null. */
  body: unknown
  workspaceId: string | null
  startedAt: number
  durationMs: number | null
  state: "pending" | "success" | "error"
  /** The HTTP status, or null while pending or when the request never got one. */
  status: number | null
  /** The parsed response body, for successes and API errors alike. */
  response: unknown
  /** Why the call failed when there's no response body to show, e.g. a network error. */
  errorMessage: string | null
  /** The `seam-request-id` response header, for support tickets. */
  requestId: string | null
  /** How many times axios-retry sent it again. */
  retryCount: number
  /** Past the newest calls, bodies are dropped to keep memory down. */
  areBodiesDropped: boolean
}

/** Calls kept in memory; older ones drop off so a long session stays light. */
const MAX_API_CALLS = 300

/**
 * Calls that keep their request and response bodies. Console pages load
 * devices and events in pages of 2000, so keeping every body would hold
 * megabytes per call.
 */
const MAX_CALLS_WITH_BODIES = 50

interface SeamDevToolsState {
  /** Newest first. */
  calls: ApiCall[]
  isRecording: boolean
  /** An error was recorded since the devtools were last looked at. */
  hasUnseenErrors: boolean
  startCall: (
    call: Pick<ApiCall, "id" | "method" | "path" | "body" | "workspaceId">
  ) => void
  completeCall: (id: string, update: Partial<ApiCall>) => void
  markRetried: (id: string) => void
  clear: () => void
  setRecording: (isRecording: boolean) => void
  markErrorsSeen: () => void
}

export const useSeamDevTools = create<SeamDevToolsState>()((set) => ({
  calls: [],
  isRecording: true,
  hasUnseenErrors: false,
  startCall: (call) => {
    set((state) => ({
      calls: addApiCall(state.calls, {
        ...call,
        startedAt: Date.now(),
        durationMs: null,
        state: "pending",
        status: null,
        response: null,
        errorMessage: null,
        requestId: null,
        retryCount: 0,
        areBodiesDropped: false,
      }),
    }))
  },
  completeCall: (id, update) => {
    set((state) => ({
      calls: updateApiCall(state.calls, id, (call) =>
        call.areBodiesDropped ? { ...update, response: null } : update
      ),
      hasUnseenErrors: state.hasUnseenErrors || update.state === "error",
    }))
  },
  markRetried: (id) => {
    set((state) => ({
      calls: updateApiCall(state.calls, id, (call) => ({
        retryCount: call.retryCount + 1,
      })),
    }))
  },
  clear: () => {
    set({ calls: [], hasUnseenErrors: false })
  },
  setRecording: (isRecording) => {
    set({ isRecording })
  },
  markErrorsSeen: () => {
    set({ hasUnseenErrors: false })
  },
}))

/**
 * Puts a call first, dropping the oldest past `limit` and the bodies of the
 * call that falls past `bodyLimit`.
 */
export function addApiCall(
  calls: readonly ApiCall[],
  call: ApiCall,
  { limit = MAX_API_CALLS, bodyLimit = MAX_CALLS_WITH_BODIES } = {}
): ApiCall[] {
  const next = calls.slice(0, limit - 1)
  next.unshift(call)
  const pastBodyLimit = next[bodyLimit]
  if (pastBodyLimit != null && !pastBodyLimit.areBodiesDropped) {
    next[bodyLimit] = {
      ...pastBodyLimit,
      body: null,
      response: null,
      areBodiesDropped: true,
    }
  }
  return next
}

/** The calls with one updated, or the same array when it's no longer kept. */
export function updateApiCall(
  calls: ApiCall[],
  id: string,
  update: (call: ApiCall) => Partial<ApiCall>
): ApiCall[] {
  const index = calls.findIndex((call) => call.id === id)
  const current = calls[index]
  if (current == null) return calls
  const next = [...calls]
  next[index] = { ...current, ...update(current) }
  return next
}
